/** Context payload for POST b2b/billing/sku-suggestions */
import { resolveCompanyRtrSku, resolveGenericRtrSku, resolveRtrSkuFromTitle } from './sku-utils';

export interface SkuSuggestionContext {
  charged?: boolean | null;
  managed?: boolean | null;
  is_rtr?: boolean | null;
  is_political?: boolean | null;
  channel?: 'programmatic' | 'social' | 'search' | 'chatgpt' | null;
  platform?: 'google' | 'meta' | 'dv360' | 'nexxen' | 'yahoo' | 'linkedin' | 'openai' | null;
  company_rtr_sku?: string | null;
  company_name?: string | null;
  campaign_title?: string | null;
}

export interface SkuSuggestion {
  sku: string | null;
  sku_id?: string | null;
  label: string;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

const PLATFORM_MAP: Record<string, SkuSuggestionContext['platform']> = {
  google: 'google',
  google_ads: 'google',
  meta: 'meta',
  facebook: 'meta',
  facebook_ads: 'meta',
  dv360: 'dv360',
  dv_360: 'dv360',
  display_video_360: 'dv360',
  nexxen: 'nexxen',
  nexxen_dsp: 'nexxen',
  yahoo: 'yahoo',
  yahoo_dsp: 'yahoo',
  linkedin: 'linkedin',
  linkedin_ads: 'linkedin',
  openai: 'openai',
  openai_ads: 'openai',
  chatgpt: 'openai',
  chat_gpt: 'openai',
};

const CHANNEL_MAP: Record<string, SkuSuggestionContext['channel']> = {
  programmatic: 'programmatic',
  social: 'social',
  search: 'search',
  chatgpt: 'chatgpt',
};

const POLITICAL_TERMS = [
  'president', 'governor', 'senator', 'representative', 'mayor', 'congress',
  'political', 'election', 'vote', 'ballot', 'campaign for',
];

export function normalizePlatformForSkuHelper(raw: string | null | undefined): SkuSuggestionContext['platform'] {
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/-/g, '_').trim();
  return PLATFORM_MAP[key] ?? null;
}

export function normalizeChannelForSkuHelper(raw: string | null | undefined): SkuSuggestionContext['channel'] {
  if (!raw) return null;
  const key = raw.toLowerCase().trim();
  return CHANNEL_MAP[key] ?? null;
}

export function detectRtrFromTitle(title: string | null | undefined): boolean | null {
  if (!title) return null;
  return /\bRTR\b/i.test(title);
}

export function detectPoliticalFromTitle(title: string | null | undefined): boolean | null {
  if (!title) return null;
  const lower = title.toLowerCase();
  if (POLITICAL_TERMS.some((term) => lower.includes(term))) {
    return true;
  }
  return null;
}

export function buildInvoiceLineItemSkuContext(
  lineItem: {
    advertiser_spend?: number | null;
    amount_to_bill?: number | null;
    campaign?: string | null;
    channel?: string | null;
    platform?: string | null;
    name?: string | null;
  },
  options?: {
    managed?: boolean | null;
    companyName?: string | null;
    companyRtrSku?: string | null;
    campaignTitle?: string | null;
    skus?: Array<{ sku?: string | null; name?: string | null }>;
  },
): SkuSuggestionContext {
  const spend = Number(lineItem.advertiser_spend ?? 0);
  const amountToBill = Number(lineItem.amount_to_bill ?? 0);
  const charged = spend > 0 || amountToBill > 0 ? true : spend === 0 && amountToBill === 0 ? false : null;
  const catalogue = options?.skus ?? [];
  const titleForRtr = lineItem.name ?? options?.campaignTitle ?? null;
  const companyRtrSku =
    options?.companyRtrSku
    ?? resolveCompanyRtrSku(options?.companyName, catalogue)
    ?? resolveRtrSkuFromTitle(titleForRtr, catalogue)
    ?? resolveGenericRtrSku(catalogue)
    ?? null;
  const platform = normalizePlatformForSkuHelper(lineItem.platform);
  const channel =
    normalizeChannelForSkuHelper(lineItem.channel)
    ?? (platform === 'openai' ? 'chatgpt' : null);

  return {
    charged,
    managed: options?.managed ?? (lineItem.campaign ? true : null),
    is_rtr: detectRtrFromTitle(lineItem.name),
    is_political: detectPoliticalFromTitle(lineItem.name),
    channel,
    platform,
    company_rtr_sku: companyRtrSku,
    company_name: options?.companyName ?? null,
    campaign_title: titleForRtr,
  };
}

export function buildCampaignSkuContext(
  campaign: {
    title?: string | null;
    channel?: string | null;
    platform?: string | null;
  },
  options?: {
    companyName?: string | null;
    companyRtrSku?: string | null;
    skus?: Array<{ sku?: string | null; name?: string | null }>;
  },
): SkuSuggestionContext {
  const catalogue = options?.skus ?? [];
  const companyRtrSku =
    options?.companyRtrSku
    ?? resolveCompanyRtrSku(options?.companyName, catalogue)
    ?? resolveRtrSkuFromTitle(campaign.title, catalogue)
    ?? resolveGenericRtrSku(catalogue)
    ?? null;
  const platform = normalizePlatformForSkuHelper(campaign.platform);
  const channel =
    normalizeChannelForSkuHelper(campaign.channel)
    ?? (platform === 'openai' ? 'chatgpt' : null);

  return {
    charged: null,
    managed: true,
    is_rtr: detectRtrFromTitle(campaign.title),
    is_political: detectPoliticalFromTitle(campaign.title),
    channel,
    platform,
    company_rtr_sku: companyRtrSku,
    company_name: options?.companyName ?? null,
    campaign_title: campaign.title ?? null,
  };
}

export const SKU_RULES_LOOKUP_ROWS = [
  { scenario: 'Charged + did not manage', bucket: 'Pass-through', codes: 'OPTG (Google), OPTM (Meta), CGPT (OpenAI / ChatGPT)' },
  { scenario: 'Not charged + managed', bucket: 'Margin only', codes: 'PMO, SMO, SOMO, CMO' },
  { scenario: 'Not charged + did not manage', bucket: 'Hide line', codes: '—' },
  { scenario: 'Managed + charged + RTR', bucket: 'RTR', codes: 'RTR (standard) or company-specific RTR_* SKU' },
  { scenario: 'Managed + charged + political', bucket: 'Political', codes: 'PCD, PCG, PCN, PCY' },
  { scenario: 'Managed + charged + programmatic', bucket: 'Programmatic', codes: 'DVP, PN, YP' },
  { scenario: 'Managed + charged + social', bucket: 'Social', codes: 'SOL, SOM' },
  { scenario: 'Managed + charged + search', bucket: 'Search', codes: 'SRG (Google)' },
  { scenario: 'Managed + charged + ChatGPT', bucket: 'Pass-through', codes: 'CGPT (OpenAI / ChatGPT)' },
] as const;
